import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import { Grid, Typography } from "@mui/material";
import PropTypes from "prop-types";

import InputFieldText from "Components/Common/InputFieldText";
import SelectField from "Components/Common/SelectField";

import { onlyNumberKeys, resetFieldValue } from "utils";

import { usePremiseAreaQuery } from "apis/PremiseArea";
import { useLazyZoneQuery } from "apis/Zone";
import { useLazyBlockQuery } from "apis/Block";
import { useLazyBuildingQuery } from "apis/Building";
import { useLazyLocationByPincodeQuery } from "apis/Location";
// import Backdrop from "Components/Common/Backdrop";

const AddressForFireSafety = ({ formik, t }) => {
  const { data: { data: { items: areaOptions } = {} } = {} } =
    usePremiseAreaQuery({}, true);
  const [getZoneData, { data: { data: zones } = {} }] = useLazyZoneQuery();
  const [getBlockData, { data: { data: blocks } = {} }] = useLazyBlockQuery();
  const [getBuildingData, { data: { data: building } = {} }] =
    useLazyBuildingQuery();
  const [getLocation] = useLazyLocationByPincodeQuery();

  const {
    values: {
      fireSafetyAddress: {
        premisesAreaId,
        zoneName,
        blockName,
        buildingName,
        pinCode,
      },
    },
    setFieldValue,
  } = formik;

  const [pinCodeError, setPinCodeError] = useState("");

  // get zone data based on area selected
  useEffect(() => {
    if (premisesAreaId) {
      getZoneData(premisesAreaId, {}, true);
    }
  }, [premisesAreaId, getZoneData]);

  // get blocks based on selected zone
  useEffect(() => {
    const zoneId = zones?.find((item) => item.name === zoneName)?.id;
    if (zoneId) {
      getBlockData(zoneId, {}, true);
    } else if (zoneName) {
      setFieldValue("fireSafetyAddress.zoneName", zoneName);
    }
  }, [zones, zoneName, getBlockData]);

  // get buildings based on selected block
  useEffect(() => {
    const blockId = blocks?.find((item) => item.name === blockName)?.id;
    if (blockId) {
      getBuildingData(blockId, {}, true);
    } else if (blockName) {
      setFieldValue("fireSafetyAddress.blockName", blockName);
    }
  }, [blocks, blockName, getBuildingData]);

  // get buildingName based on block no
  useEffect(() => {
    const buildingId = building?.find((item) => item.name === buildingName)?.id;
    if (!buildingId && buildingName) {
      setFieldValue("fireSafetyAddress.buildingName", buildingName);
    }
    if (buildingId) {
      setFieldValue("buildingId", buildingId);
    }
  }, [building, buildingName]);

  // get city by pinCode
  useEffect(() => {
    (async () => {
      if (pinCode.toString().length === 6) {
        try {
          const res = await getLocation(pinCode, {}, true);
          setPinCodeError("");
          setFieldValue("fireSafetyAddress.city", res.data.city);
        } catch (error) {
          setPinCodeError(t("Please enter valid pincode"));
          setFieldValue("fireSafetyAddress.city", "");
        }
      }
      if (!pinCode.toString().length) {
        setFieldValue("fireSafetyAddress.city", "");
      }
    })();
  }, [pinCode, getLocation, setFieldValue]);

  // if zoneName selected "other"
  useEffect(() => {
    if (zoneName === "other") {
      setFieldValue("fireSafetyAddress.blockName", "other");
      setFieldValue("fireSafetyAddress.buildingName", "other");
    }
  }, [zoneName]);

  // if blockName selected "other"
  useEffect(() => {
    if (blockName === "other") {
      setFieldValue("fireSafetyAddress.buildingName", "other");
    }
  }, [blockName]);

  return (
    <>
      <Grid item xs={12}>
        <Typography className="mb-3" lineHeight={1} variant="h6">
          {t("Address")}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={3}>
        <SelectField
          label={t("Area") + "*"}
          name="fireSafetyAddress.premisesAreaId"
          formik={formik}
          options={areaOptions || []}
          getOptionLabel={(option) => option.name}
          getOptionValue={(option) => option.id}
          onChange={(_, { value }) => {
            setFieldValue("fireSafetyAddress.premisesAreaId", value);
            resetFieldValue(setFieldValue, [
              "fireSafetyAddress.blockName",
              "fireSafetyAddress.zoneName",
              "fireSafetyAddress.buildingName",
            ]);
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={3}>
        <SelectField
          label={t("Select Zone") + "*"}
          name="fireSafetyAddress.zoneName"
          formik={formik}
          disabled={!premisesAreaId}
          options={[
            ...(zones || []),
            { id: 0, name: "other" },
            ...([...(zones || []), { id: 0, name: "other" }]?.find(
              (zoneObj) => zoneObj.name === zoneName
            )
              ? []
              : [{ id: 0, name: zoneName }]),
          ]}
          getOptionLabel={(option) => option.name}
          getOptionValue={(option) => option.name}
          placeholder="Select Zone"
          onChange={(_, { value }) => {
            setFieldValue("fireSafetyAddress.zoneName", value);
            resetFieldValue(setFieldValue, [
              "fireSafetyAddress.blockName",
              "fireSafetyAddress.buildingName",
            ]);
          }}
        />
      </Grid>

      {zoneName === "other" && (
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <InputFieldText
            label={t("Enter Zone") + "*"}
            name="fireSafetyAddress.otherZone"
            formik={formik}
            placeholder="Enter Zone"
          />
        </Grid>
      )}

      {zoneName !== "other" && (
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <SelectField
            label={t("Block/Plot No") + "*"}
            name="fireSafetyAddress.blockName"
            formik={formik}
            disabled={!zoneName}
            options={[
              ...(blocks || []),
              { id: 0, name: "other" },
              ...([...(blocks || []), { id: 0, name: "other" }]?.find(
                (blockObj) => blockObj.name === blockName
              )
                ? []
                : [{ id: 0, name: blockName }]),
            ]}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.name}
            placeholder="Select Block"
            onChange={(_, { value }) => {
              setFieldValue("fireSafetyAddress.blockName", value);
              setFieldValue("fireSafetyAddress.buildingName", "");
            }}
          />
        </Grid>
      )}

      {(zoneName === "other" || blockName === "other") && (
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <InputFieldText
            label={t("Enter Block") + "*"}
            name="fireSafetyAddress.otherBlock"
            formik={formik}
            placeholder="Enter Block"
          />
        </Grid>
      )}

      {zoneName !== "other" && blockName !== "other" && (
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <SelectField
            label={t("Name of Building") + "*"}
            name="fireSafetyAddress.buildingName"
            formik={formik}
            disabled={!blockName}
            options={[
              ...(building || []),
              { id: 0, name: "other" },
              ...([...(building || []), { id: 0, name: "other" }]?.find(
                (buildingObj) => buildingObj.name === buildingName
              )
                ? []
                : [{ id: 0, name: buildingName }]),
            ]}
            placeholder="Select Building"
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.name}
          />
        </Grid>
      )}
      {(zoneName === "other" ||
        buildingName === "other" ||
        blockName === "other") && (
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <InputFieldText
            label={t("Enter Building") + "*"}
            name="fireSafetyAddress.otherBuilding"
            formik={formik}
            placeholder="Enter Building Name"
          />
        </Grid>
      )}

      <Grid item xs={12} sm={6} md={4} lg={3}>
        <InputFieldText
          label={t("Building Number")}
          name="buildingNumber"
          formik={formik}
          placeholder="Enter Building Number"
        />
      </Grid>

      <Grid item xs={12} sm={6} md={4} lg={3}>
        <InputFieldText
          label={t("Nearest Landmark")}
          name="fireSafetyAddress.nearestLandmark"
          formik={formik}
          placeholder="Enter Landmark"
        />
      </Grid>

      <Grid item xs={12} sm={6} md={4} lg={3}>
        <InputFieldText
          label={t("Pincode") + "*"}
          name="fireSafetyAddress.pinCode"
          formik={formik}
          errorMessage={pinCodeError}
          placeholder="Enter Pincode"
          type="number"
          InputProps={{
            inputProps: { min: 0, onKeyDown: onlyNumberKeys },
          }}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={4} lg={3}>
        <InputFieldText
          label={t("City") + "*"}
          name="fireSafetyAddress.city.name"
          formik={formik}
          placeholder="Enter City"
          disabled
        />
      </Grid>
    </>
  );
};

AddressForFireSafety.propTypes = {
  formik: PropTypes.object,
  t: PropTypes.func,
};

export default withTranslation()(AddressForFireSafety);
